import React from 'react';
import * as R from 'ramda';
// forms
import AccessorialForm from '../../../forms/forms/accessorial-form';
import SharedAccessorialForm from '../forms/shared-accessorial-form';
import { districtOptions } from '../../../forms/forms/accessorial-form/field-settings';
// icons
import * as I from '../../../svgs';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import {
  ITEM_DIMENSIONS_OPTIONS,
  ACCESSORIAL_MATRIX_TYPE_OPTIONS,
  DEFAULT_CONTRACT_RATE_UNIT_OPTIONS,
  DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS,
} from '../../../helpers/options';
// utilities
import endpointsMap from '../../../utilities/endpoints';
// feature fleet-profile
import { removeEntitySuccess } from '../actions';
import { TableCell } from '../components/table-cells';
//////////////////////////////////////////////////

const getOptionLabel = (value, options) => R.compose(
  R.propOr(value, GC.FIELD_LABEL),
  R.find(R.propEq(value, GC.FIELD_VALUE)),
)(options);

const settings = {
  [GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID]: {
    sortable: false,
    name: 'titles:accessorial',
    customComponent: ({ data = {}, callbackData = {} }) => {
      const { assessorialGuid } = data;

      const text = R.path(
        ['accessorialConfigs', 'accessorialConfigList', assessorialGuid, GC.FIELD_DISPLAYED_VALUE],
        callbackData,
      );

      return <TableCell text={text} />;
    },
  },
  [GC.FIELD_FUEL_RELATED]: {
    width: 100,
    type: 'boolean',
    sortable: false,
    searchable: false,
    disableFilter: true,
    name: 'titles:fuel-related',
    customComponent: ({ data = {}, callbackData = {} }) => {
      const { assessorialGuid } = data;

      const fuelRelated = R.path(
        ['accessorialConfigs', 'accessorialConfigList', assessorialGuid, GC.FIELD_FUEL_RELATED],
        callbackData,
      );

      return G.ifElse(fuelRelated, I.uiTrue, I.uiFalse)();
    },
  },
  [GC.FIELD_NON_TAXABLE]: {
    width: 100,
    type: 'boolean',
    sortable: false,
    searchable: false,
    disableFilter: true,
    name: 'titles:non-taxable',
    customComponent: ({ data = {}, callbackData = {} }) => {
      const { assessorialGuid } = data;

      const nonTaxable = R.path(
        ['accessorialConfigs', 'accessorialConfigList', assessorialGuid, GC.FIELD_NON_TAXABLE],
        callbackData,
      );

      return G.ifElse(nonTaxable, I.uiTrue, I.uiFalse)();
    },
  },
  [GC.FIELD_DEDUCTION]: {
    width: 150,
    type: 'boolean',
    name: 'titles:deduction',
  },
  [GC.FIELD_ONLY_INCLUDE_TO_INVOICE]: {
    width: 150,
    type: 'boolean',
    name: 'titles:only-include-to-invoice',
  },
  [GC.FIELD_CHARGE_RATE]: {
    width: 180,
    name: 'titles:rate',
    filterType: 'number',
  },
  [GC.FIELD_CURRENCY]: {
    width: 200,
    name: 'titles:currency',
    filterType: 'selectMultiple',
    options: GC.CURRENCY_OPTIONS,
  },
  [GC.FIELD_CHARGE_RATE_TYPE]: {
    name: 'titles:rate-type',
    filterType: 'selectMultiple',
    options: R.filter(R.prop(GC.FIELD_VALUE), DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS),
    customComponent: ({ data: { rateType } = {} }) => R.compose(
      R.propOr(rateType, GC.FIELD_LABEL),
      R.find(R.propEq(rateType, GC.FIELD_VALUE)),
    )(DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS),
  },
  [GC.FIELD_CHARGE_RATE_UNIT]: {
    name: 'titles:rate-unit',
    filterType: 'selectMultiple',
    options: R.filter(R.prop(GC.FIELD_VALUE), DEFAULT_CONTRACT_RATE_UNIT_OPTIONS),
    customComponent: ({ data: { rateUnit } = {} }) => R.compose(
      R.propOr(rateUnit, GC.FIELD_LABEL),
      R.find(R.propEq(rateUnit, GC.FIELD_VALUE)),
    )(DEFAULT_CONTRACT_RATE_UNIT_OPTIONS),
  },
  [GC.FIELD_CHARGE_MIN_RATE]: {
    width: 180,
    filterType: 'number',
    name: 'titles:min-rate',
  },
  [GC.FIELD_CHARGE_MAX_RATE]: {
    width: 180,
    filterType: 'number',
    name: 'titles:max-rate',
  },
  [GC.FIELD_MIN_STOPS]: {
    width: 180,
    filterType: 'number',
    name: 'titles:min-stops',
  },
  [GC.FIELD_MAX_STOPS]: {
    width: 180,
    filterType: 'number',
    name: 'titles:max-stops',
  },
  [GC.FIELD_ACCESSORIAL_DISTRICT]: {
    name: 'titles:district',
    options: districtOptions,
    filterType: 'selectMultiple',
    customComponent: ({ data }) => {
      const district = G.getPropFromObject(GC.FIELD_ACCESSORIAL_DISTRICT, data);

      return R.pathOr(district, [district], GC.districtMap);
    },
  },
  [GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX]: {
    width: 150,
    type: 'boolean',
    name: 'titles:use-fuel-index',
  },
  [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: {
    width: 150,
    name: 'titles:matrix-type',
    filterType: 'selectMultiple',
    options: R.tail(ACCESSORIAL_MATRIX_TYPE_OPTIONS),
    customComponent: ({ data }) => getOptionLabel(
      R.prop(GC.FIELD_ACCESSORIAL_MATRIX_TYPE, data),
      ACCESSORIAL_MATRIX_TYPE_OPTIONS,
    ),
  },
  [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: {
    width: 150,
    name: 'titles:matrix-uom',
    filterType: 'selectMultiple',
    options: R.tail(ITEM_DIMENSIONS_OPTIONS),
    customComponent: ({ data }) => getOptionLabel(
      R.prop(GC.FIELD_ACCESSORIAL_MATRIX_UOM, data),
      ITEM_DIMENSIONS_OPTIONS,
    ),
  },
  [GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]: {
    width: 150,
    sortable: false,
    collection: true,
    searchable: false,
    disableFilter: true,
    name: 'titles:matrix-variables',
    customComponent: R.compose(
      R.length,
      R.pathOr([], ['data', GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES]),
    ),
  },
  [GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]: {
    width: 150,
    sortable: false,
    collection: true,
    searchable: false,
    disableFilter: true,
    name: 'titles:fuel-index-quantity',
    customComponent: R.compose(
      R.length,
      R.pathOr([], ['data', GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES]),
    ),
  },
};

const columnSettings = R.map(R.pick(['type', 'name', 'width', 'sortable', 'searchable', 'customComponent']), settings);

const placeholder = { text: 'Choose', key: 'titles:choose' };

const getAccessorialConfigFieldValue = (fieldName) => (data = {}, callbackData = {}) => {
  const { assessorialGuid } = data;

  return R.path(['accessorialConfigs', 'accessorialConfigList', assessorialGuid, fieldName], callbackData);
};

const filtersMap = {
  shared: { type: 'boolean' },
  [GC.FIELD_MAX_STOPS]: { type: 'number' },
  [GC.FIELD_MIN_STOPS]: { type: 'number' },
  [GC.FIELD_CHARGE_RATE]: { type: 'number' },
  [GC.FIELD_DISPLAYED_NAME]: { type: 'string' },
  [GC.FIELD_CHARGE_MIN_RATE]: { type: 'number' },
  [GC.FIELD_CHARGE_MAX_RATE]: { type: 'number' },
  [GC.FIELD_ACCESSORIAL_USE_FUEL_INDEX]: { type: 'boolean' },
  [GC.FIELD_ACCESSORIAL_MATRIX_TYPE]: {
    type: 'selectMultiple',
    options: R.tail(ACCESSORIAL_MATRIX_TYPE_OPTIONS),
  },
  [GC.FIELD_ACCESSORIAL_MATRIX_UOM]: {
    type: 'selectMultiple',
    options: R.tail(ITEM_DIMENSIONS_OPTIONS),
  },
  [GC.FIELD_CURRENCY]: { type: 'selectMultiple', options: GC.CURRENCY_OPTIONS },
  [GC.FIELD_ACCESSORIAL_DISTRICT]: { type: 'selectMultiple', options: districtOptions },
  [GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID]: {
    type: 'string',
    getFieldUiValue: getAccessorialConfigFieldValue(GC.FIELD_DISPLAYED_VALUE),
  },
  [GC.FIELD_FUEL_RELATED]: {
    type: 'boolean',
    getFieldUiValue: getAccessorialConfigFieldValue(GC.FIELD_FUEL_RELATED),
  },
  [GC.FIELD_NON_TAXABLE]: {
    type: 'boolean',
    getFieldUiValue: getAccessorialConfigFieldValue(GC.FIELD_NON_TAXABLE),
  },
  [GC.FIELD_CHARGE_RATE_TYPE]: {
    type: 'selectMultiple',
    options: R.filter(R.prop(GC.FIELD_VALUE), DEFAULT_DRIVER_ASSESSORIAL_RATE_TYPE_OPTIONS),
  },
  [GC.FIELD_CHARGE_RATE_UNIT]: {
    type: 'selectMultiple',
    options: R.filter(R.prop(GC.FIELD_VALUE), DEFAULT_CONTRACT_RATE_UNIT_OPTIONS),
  },
};

const sharedColumnSettings = R.mapObjIndexed(
  (item, key) => ({ ...item, filter: R.prop(key, filtersMap) }),
  {
    ...R.omit([GC.FIELD_DEDUCTION, GC.FIELD_ONLY_INCLUDE_TO_INVOICE], columnSettings),
    [GC.FIELD_DISPLAYED_NAME]: {
      width: 200,
      name: ['titles:shared-accessorial', 'titles:name'],
    },
  },
);

const sharedDefaultReportFields = R.compose(
  R.prepend(GC.FIELD_DISPLAYED_NAME),
  R.keys,
  R.omit([GC.FIELD_DEDUCTION, GC.FIELD_ONLY_INCLUDE_TO_INVOICE]),
)(columnSettings);

const makeSharedReport = (type = GC.DRIVER_PROFILE_UI_REPORTS) => ({
  [GC.FIELD_TYPE]: type,
  [GC.FIELD_GUID]: `${type}SharedAccessorials`,
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), sharedDefaultReportFields),
});

const filterProps = (props) => {
  const accessorialOptions = R.pathOr([], ['accessorialConfigs', 'accessorialConfigOptions'], props);

  const isDriver = R.propEq(GC.FIELD_DRIVER, 'fleetType', props);

  const mapped = R.compose(
    R.mapObjIndexed((
      { name, options, collection, filterType, disableFilter, type = 'string' },
      value,
    ) => ({
      value,
      options,
      collection,
      placeholder,
      disableFilter,
      type: R.or(filterType, type),
      [GC.FIELD_NAME]: G.ifElse(
        G.isArray(name),
        `${G.getWindowLocale(name[0])}: ${G.getWindowLocale(name[1])}`,
        G.getWindowLocale(name),
      ),
    })),
    R.omit(G.ifElse(isDriver, [GC.FIELD_ONLY_INCLUDE_TO_INVOICE], [])),
  )(settings);

  return {
    ...mapped,
    [GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID]: {
      placeholder,
      type: 'selectMultiple',
      options: accessorialOptions,
      value: GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID,
      disableFilter: R.prop('disableAccessorialFilter', props),
      name: G.getWindowLocale('titles:accessorial', 'Accessorial'),
      handleDisableOptions: (option, value) => R.gte(R.length(value), 10),
    },
  };
};

const FILTER_PARAMS = (props) => R.values(filterProps(props));

const makeRequestPayload = (props) => {
  const { vendorBranchGuid, driverBranchGuid, primaryObjectGuid, primaryObjectGuidKey } = props;

  return {
    [primaryObjectGuidKey]: primaryObjectGuid,
    [GC.CURRENT_BRANCH]: R.or(driverBranchGuid, vendorBranchGuid),
    reportFieldsToOmit: [GC.FIELD_NON_TAXABLE, GC.FIELD_FUEL_RELATED],
    systemFields: [
      { sequence: 0, name: GC.FIELD_VERSION },
      { sequence: 1, collection: true, name: GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES },
      { sequence: 2, collection: true, name: GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES },
    ],
  };
};

const makeInitialValues = (initialValues = {}, { branchConfigs, isReadOnlyMode, accessorialConfigs }) => {
  const { currency, assessorialGuid } = initialValues;

  const branchCurrency = G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, branchConfigs);

  return {
    ...initialValues,
    [GC.FIELD_CURRENCY]: R.or(currency, branchCurrency),
    shared: R.propOr(isReadOnlyMode, 'shared', initialValues),
    [GC.FIELD_FUEL_RELATED]: R.pathOr(
      false,
      ['accessorialConfigList', assessorialGuid, GC.FIELD_FUEL_RELATED],
      accessorialConfigs,
    ),
    [GC.FIELD_NON_TAXABLE]: R.pathOr(
      false,
      ['accessorialConfigList', assessorialGuid, GC.FIELD_NON_TAXABLE],
      accessorialConfigs,
    ),
  };
};

const SharedAccessorialCustomForm = ({
  fleetType,
  closeModal,
  isEditMode,
  submitAction,
  initialValues,
  isReadOnlyMode,
  optionsForSelect,
  primaryObjectGuid,
  primaryObjectBranchGuid,
}) => {
  if (R.not(isReadOnlyMode)) {
    return (
      <SharedAccessorialForm
        fleetType={fleetType}
        closeModal={closeModal}
        isEditMode={isEditMode}
        submitAction={submitAction}
        initialValues={initialValues}
        primaryObjectGuid={primaryObjectGuid}
        primaryObjectBranchGuid={primaryObjectBranchGuid}
      />
    );
  }

  return (
    <AccessorialForm
      {...optionsForSelect}
      isReadOnlyMode={true}
      submitAction={submitAction}
      initialValues={initialValues}
    />
  );
};

const sharedAccessorialCustomRemoveHandler = (props, guid) => {
  const {
    dispatch,
    endpoints,
    groupName,
    primaryObjectGuid,
    createOrUpdateEntityRequest,
  } = props;

  const getItemListRequest = () => dispatch(removeEntitySuccess({ guid, groupName }));

  createOrUpdateEntityRequest({
    groupName,
    method: 'post',
    getItemListRequest,
    endpoint: endpointsMap[R.prop('remove', endpoints)],
    values: {
      primaryObjectGuid,
      [GC.FIELD_ACCESSORIAL_SHARED_ACCESSORIAL_GUIDS]: R.of(Array, guid),
    },
  });
};

const accessorialSettings = {
  columnSettings,
  isReport: true,
  useSearchableColumns: true,
  withResizableColumns: true,
  groupName: 'accessorialList',
  useNewTitleFilterInputs: true,
  reportType: GC.FLEET_DRIVER_ASSESSORIAL_REPORT,
  additionalTableSettings: { searchableTitles: true },
  itemTitleArr: ['titles-accessorial', 'Accessorial'],
  formGroupTitleArr: ['titles-accessorials', 'Accessorials'],
  tableCallbackDataProps: ['itemList', 'accessorialConfigs'],
  endpoints: {
    list: 'driverAccessorialsList',
    createOrUpdate: 'driverAccessorial',
    entity: 'getDriverAccessorialEndpoint',
  },
  // components
  CustomForm: ({ submitAction, initialValues, optionsForSelect, primaryObjectGuid }) => (
    <AccessorialForm
      {...optionsForSelect}
      isDriver={true}
      submitAction={submitAction}
      initialValues={R.assoc(GC.FIELD_DRIVER_GUID, primaryObjectGuid, initialValues)}
    />
  ),
  // helpers
  filterProps,
  makeInitialValues,
  makeRequestPayload,
  makeOptionsForSelect: R.pathOr({}, ['accessorialConfigs']),
};

const vendorAccessorialSettings = {
  ...accessorialSettings,
  reportType: GC.FLEET_VENDOR_ASSESSORIAL_REPORT,
  primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  endpoints: {
    list: 'fleetVendorAccessorialList',
    entity: 'getVendorAccessorialEndpoint',
    createOrUpdate: 'fleetVendorAccessorial',
  },
  CustomForm: ({ submitAction, initialValues, optionsForSelect, primaryObjectGuid }) => (
    <AccessorialForm
      {...optionsForSelect}
      isVendor={true}
      submitAction={submitAction}
      initialValues={R.assoc(GC.FIELD_FLEET_VENDOR_GUID, primaryObjectGuid, initialValues)}
    />
  ),
};

const sharedAccessorialSettings = {
  defaultValues: {},
  useGetListRequest: true,
  report: makeSharedReport(),
  withResizableColumns: true,
  withUiFiltersAndEditView: true,
  groupName: 'sharedAccessorialList',
  columnSettings: sharedColumnSettings,
  actionsPicker: ['showDetails', 'remove'],
  defaultReportFields: sharedDefaultReportFields,
  tableCallbackDataProps: ['itemList', 'accessorialConfigs'],
  itemTitleArr: ['titles:shared-accessorial', 'Shared Accessorial'],
  formGroupTitleArr: ['titles:shared-accessorials', 'Shared Accessorials'],
  additionalTableSettings: { searchableTitles: true, checkBoxCellWidth: 80 },
  endpoints: {
    list: 'driverAccessorialsList',
    remove: 'removeDriverSharedAccessorials',
    createOrUpdate: 'addDriverSharedAccessorials',
  },
  // components
  CustomForm: SharedAccessorialCustomForm,
  // helpers
  makeInitialValues,
  customRemoveHandler: sharedAccessorialCustomRemoveHandler,
  makeOptionsForSelect: R.pathOr({}, ['accessorialConfigs']),
};

const vendorSharedDefaultReportFields = R.append(GC.FIELD_FREE_DISTANCE, sharedDefaultReportFields);

const vendorSharedAccessorialSettings = {
  ...sharedAccessorialSettings,
  customRemoveHandler: null,
  actionsPicker: ['remove', 'showDetails', 'edit'],
  primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  defaultReportFields: vendorSharedDefaultReportFields,
  CustomForm: (props) => <SharedAccessorialCustomForm {...props} withFreeMiles={true} />,
  getEditPermissions: ({ rateType }) => G.ifElse(G.notEquals(rateType, GC.CHARGE_RATE_TYPE_DEADHEAD_DISTANCE), ''),
  endpoints: {
    list: 'fleetVendorAccessorialList',
    remove: 'removeFleetVendorSharedAccessorial',
    createOrUpdate: 'fleetVendorSharedAccessorial',
  },
  report: {
    ...makeSharedReport(GC.VENDOR_PROFILE_UI_REPORTS),
    fields: G.mapIndexed(
      (name, sequence) => ({ name, sequence }),
      vendorSharedDefaultReportFields,
    ),
  },
  columnSettings: {
    ...sharedColumnSettings,
    [GC.FIELD_FREE_DISTANCE]: {
      width: 150,
      name: 'titles:free-miles',
      filter: { type: 'number' },
    },
  },
};

const vendorCompensationAccessorialSettings = {
  ...R.omit(
    [
      'isReport',
      'reportType',
      'filterProps',
      'makeRequestPayload',
      'useSearchableColumns',
      'withResizableColumns',
      'additionalTableSettings',
      'useNewTitleFilterInputs',
    ],
    vendorAccessorialSettings,
  ),
  includeItemListToTablePropsMemoDeps: true,
  primaryObjectGuidKey: GC.FIELD_COMPENSATION_GUID,
  report: {
    fields: R.compose(
      G.mapIndexed((name, sequence) => ({ name, sequence })),
      R.keys,
    )(settings),
  },
  endpoints: {
    remove: 'removeVendorCompensationAccessorial',
    list: 'fleetVendorCompensationAccessorialList',
    createOrUpdate: 'fleetVendorCompensationAccessorial',
  },
  CustomForm: ({ submitAction, initialValues, optionsForSelect, primaryObjectGuid }) => (
    <AccessorialForm
      {...optionsForSelect}
      isVendor={true}
      submitAction={submitAction}
      initialValues={R.assoc(GC.FIELD_COMPENSATION_GUID, primaryObjectGuid, initialValues)}
    />
  ),
};

export {
  FILTER_PARAMS,
  accessorialSettings,
  vendorAccessorialSettings,
  sharedAccessorialSettings,
  vendorSharedAccessorialSettings,
  vendorCompensationAccessorialSettings,
};
